/**
 * The Account Priority band palette — the one definition.
 *
 * A band is drawn in three places: the score cell (`ScoreDisplay`, inline
 * styles, so hex), the Account Priority card and the Signals panel pill (both
 * Tailwind classes). They were three tables that agreed by convention, so a
 * change to one silently left the other two describing the same score in a
 * different colour. The hex values are the Tailwind tokens named beside them —
 * emerald from the default palette, `mb-blue` from `tailwind.config.js`.
 *
 * The class strings are literal on purpose: Tailwind's content scan only sees
 * classes spelled out in source.
 */
export type AccountPriorityBand = 'High' | 'Medium' | 'Low';

interface BandStyle {
  /** Pill background + text, as Tailwind classes. */
  pill: string;
  /** Bar fill, as a Tailwind class. */
  bar: string;
  /** The same colours as hex, for inline styles. */
  hex: { fg: string; bg: string; bar: string };
}

export const ACCOUNT_PRIORITY_BAND_STYLES: Record<AccountPriorityBand, BandStyle> = {
  // emerald-700 / emerald-50 / emerald-500
  High: {
    pill: 'bg-emerald-50 text-emerald-700',
    bar: 'bg-emerald-500',
    hex: { fg: '#047857', bg: '#ECFDF5', bar: '#10B981' },
  },
  // mb-blue-700 / mb-blue-50 / mb-blue-600
  Medium: {
    pill: 'bg-mb-blue-50 text-mb-blue-700',
    bar: 'bg-mb-blue-600',
    hex: { fg: '#004299', bg: '#f0f7ff', bar: '#0056cc' },
  },
  // slate-600 / slate-100 / slate-400
  Low: {
    pill: 'bg-slate-100 text-slate-600',
    bar: 'bg-slate-400',
    hex: { fg: '#475569', bg: '#F1F5F9', bar: '#94A3B8' },
  },
};

/** The style for a band label, Low for anything that is not a band (Unknown, null). */
export const accountPriorityBandStyle = (label: string | null | undefined): BandStyle => (
  label && Object.prototype.hasOwnProperty.call(ACCOUNT_PRIORITY_BAND_STYLES, label)
    ? ACCOUNT_PRIORITY_BAND_STYLES[label as AccountPriorityBand]
    : ACCOUNT_PRIORITY_BAND_STYLES.Low
);
